import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Locale } from "@/i18n/config";

import {
  WorkspaceInfo,
  useWorkspaceService,
} from "../services/workspace.service";

export const workspaceKeys = {
  all: ["workspace"] as const,
  members: () => [...workspaceKeys.all, "members"] as const,
};

export const useWorkspace = (options?: { enabled?: boolean }) => {
  const workspaceService = useWorkspaceService();
  const queryClient = useQueryClient();

  const query = useQuery<WorkspaceInfo, Error>({
    queryKey: workspaceKeys.members(),
    queryFn: workspaceService.getWorkspaceMembers,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => workspaceService.inviteMember(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members() });
    },
  });

  // Tenant-wide language switch. Optimistic update so the calling UI can
  // close its confirmation modal immediately on success; the eventual refetch
  // reconciles with the server if the value somehow diverged.
  const setLanguageMutation = useMutation({
    mutationFn: (language: Locale) => workspaceService.setWorkspaceLanguage(language),
    onSuccess: ({ default_language }) => {
      queryClient.setQueryData<WorkspaceInfo | undefined>(
        workspaceKeys.members(),
        (prev) => (prev ? { ...prev, default_language } : prev),
      );
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members() });
    },
  });

  const setLogoMutation = useMutation({
    mutationFn: (file: File) => workspaceService.setWorkspaceLogo(file),
    onSuccess: ({ workspace_logo_url }) => {
      queryClient.setQueryData<WorkspaceInfo | undefined>(
        workspaceKeys.members(),
        (prev) => (prev ? { ...prev, workspace_logo_url } : prev),
      );
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members() });
    },
  });

  const setBrandingMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      company_name: string | null;
      primary_color: string | null;
      secondary_color: string | null;
      cc_invirtus_support_enabled: boolean;
    }) =>
      workspaceService.setWorkspaceBranding(payload),
    onSuccess: ({
      workspace_name,
      company_name,
      primary_color,
      secondary_color,
      cc_invirtus_support_enabled,
    }) => {
      queryClient.setQueryData<WorkspaceInfo | undefined>(
        workspaceKeys.members(),
        (prev) =>
          prev
            ? {
                ...prev,
                workspace_name,
                company_name,
                primary_color,
                secondary_color,
                cc_invirtus_support_enabled,
              }
            : prev,
      );
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members() });
    },
  });

  return {
    ...query,
    inviteMutation,
    setLanguageMutation,
    setLogoMutation,
    setBrandingMutation,
  };
};
